<template>
    <div
        :class="[classes, $attrs.class]"
        :style="$attrs.style"
    >
        <CheckboxRoot
            v-model="model"
            :name="name"
            :value="value"
            v-bind="{ ...choiceAttrs, ...fallthroughAttrs }"
            :class="element('box')"
        >
            <template #default="{ state }">
                <CheckboxIndicator :class="element('indicator')">
                    <slot
                        name="indicator"
                        :state="state"
                    >
                        <svg
                            v-if="state === true"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                        >
                            <path d="m3 8 3 3 7-7" />
                        </svg>
                        <svg
                            v-else
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                        >
                            <path d="M3 8h10" />
                        </svg>
                    </slot>
                </CheckboxIndicator>
            </template>
        </CheckboxRoot>

        <div
            v-if="hasBody"
            :class="element('body')"
        >
            <Label
                v-if="hasLabel"
                :for="controlId"
                :class="element('label')"
            >
                <slot>{{ label }}</slot>
            </Label>
            <p
                v-if="hasDescription"
                :id="descriptionId"
                :class="element('description')"
            >
                <slot name="description">
                    {{ description }}
                </slot>
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { KCheckboxProps, KCheckedState } from './types'
import useBem from '@yeeroen/use-bem'
import { CheckboxIndicator, CheckboxRoot, Label } from 'reka-ui'
import { computed, inject, useAttrs, useSlots } from 'vue'
import { K_CHECKBOX_GROUP_CONTEXT } from '../checkbox-group/context'
import { useFieldControl } from '../field/use-field-control'

defineOptions({ name: 'KCheckbox', inheritAttrs: false })

const props = withDefaults(defineProps<KCheckboxProps>(), {
    value: 'on',
    labelPosition: 'end',
    disabled: undefined,
    required: undefined,
    invalid: undefined,
    size: undefined,
})

defineSlots<{
    default?: () => unknown
    description?: () => unknown
    indicator?: (props: { state: KCheckedState }) => unknown
}>()

const model = defineModel<KCheckedState>({ default: false })
const attrs = useAttrs()
const slots = useSlots()
const group = inject(K_CHECKBOX_GROUP_CONTEXT, null)
const { block, element, modifier } = useBem('k-checkbox')
const hasLabel = computed(() => Boolean(slots.default || props.label))
const hasDescription = computed(() => Boolean(slots.description || props.description))
const hasBody = computed(() => hasLabel.value || hasDescription.value)
const {
    controlId,
    describedBy,
    invalid: fieldInvalid,
    disabled: fieldDisabled,
    required,
    size: fieldSize,
} = useFieldControl(props, { ownId: hasLabel, ownLabel: hasLabel })

const invalid = computed(() => props.invalid ?? group?.invalid.value ?? fieldInvalid.value)
const disabled = computed(() => group?.disabled.value || fieldDisabled.value)
const size = computed(() => props.size ?? group?.size.value ?? fieldSize.value)

const descriptionId = computed(() => `${controlId.value}-description`)
const choiceAttrs = computed(() => ({
    'id': controlId.value,
    'aria-describedby': [
        describedBy.value,
        hasDescription.value ? descriptionId.value : undefined,
    ].filter(Boolean).join(' ') || undefined,
    'aria-invalid': invalid.value || undefined,
    'required': required.value || undefined,
    'disabled': disabled.value || undefined,
}))
const fallthroughAttrs = computed(() => {
    const {
        'as': _as,
        'as-child': _asChildKebab,
        'asChild': _asChild,
        'class': _class,
        'default-value': _defaultValueKebab,
        'defaultValue': _defaultValue,
        'false-value': _falseValueKebab,
        'falseValue': _falseValue,
        'style': _style,
        'true-value': _trueValueKebab,
        'trueValue': _trueValue,
        ...rest
    } = attrs

    return rest
})
const classes = computed(() => block(
    modifier(size.value),
    modifier(`label-${props.labelPosition}`),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
))
</script>

<style lang="scss">
@use './k-checkbox.scss';
</style>
